import { getObjectFromSessionStore, getSessionStore } from "app/utils/sessionStore";
import Sidebar from "./Sidebar";
import { Outlet, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "app/store";
import { createContext, useEffect, useMemo, useRef, useState } from "react";
import { AGENT_PROFILE_LOOKUP, CONTACT_PROFILE_LOOKUP } from "app/redux";
import { getObjectFromLocalStore } from "app/utils/localStore";
import { isEmpty } from "ramda";
import { ProgressSpinner } from "primereact/progressspinner";
import { logoutResponseState } from "app/redux/features/logout";
import ContactLayout from "./ContactLayout";
import ContactV2Layout from "./ContactV2Layout";
import { THEME, USERTYPE } from "app/utils/types";
import { getIsNewDesign } from "app/utils/isNewDesign";
import { useAssistantPermissionsStore } from "app/zustand/assistantPermissionsStore";

export const ThemeContext = createContext<{
  isDarkMode: boolean;
  setIsDarkMode: React.Dispatch<React.SetStateAction<boolean>>;
}>({ isDarkMode: false, setIsDarkMode: () => {} });

const RootLayout = () => {
  const lookUpResponse = useSelector((state: RootState) => state?.contactProfileLookup);
  const agentLookUpResponse = useSelector((state: RootState) => state?.agentProfileLookup);
  const { isLoggingOut } = useSelector(logoutResponseState);
  const getLookUpResponse = getObjectFromSessionStore("lookUpResponse");
  const dispatch = useDispatch();
  const { pathname, search } = useLocation();
  const isNewDesign = getIsNewDesign();

  const userAccess = getObjectFromLocalStore("useraccess");
  const assistantAgentObject = getObjectFromLocalStore("assistantAgentUserAccess");
  // Once the assistant has switched to a selected advisor, sidebar-visibility should be
  // driven by that advisor's own menu (e.g. it includes "/advisor-dashboard"), not the
  // assistant's own base menu, which never contains advisor-only routes.
  const isActingAsSelectedAdvisor =
    !!userAccess?.isAssistant &&
    !isEmpty(assistantAgentObject) &&
    !!assistantAgentObject?.selectedAssistantAgentOption;
  // Switching advisors persists the new selection to localStorage but doesn't change the
  // route (the assistant is usually already on it), so react-router never remounts the routed
  // page and it keeps rendering the previous advisor's data. Keying the Outlet to the selected
  // advisor forces that remount (and its data-fetch effects to re-run) on every switch, without
  // reloading the whole app - which would otherwise briefly reset session-scoped state such as
  // the assistant permissions store.
  const selectedAdvisorOutletKey = assistantAgentObject?.selectedAssistantAgentOption || "self";

  const sessionTheme = getSessionStore("globalTheme");
  const isInitiallyDarkModeOn = !(
    sessionTheme === THEME.LIGHT ||
    lookUpResponse?.data?.branding?.theme === THEME.LIGHT ||
    isEmpty(sessionTheme)
  );

  const [isDarkMode, setIsDarkMode] = useState(isInitiallyDarkModeOn);
  useEffect(() => {
    if (lookUpResponse?.data?.branding?.theme) {
      setIsDarkMode(lookUpResponse?.data?.branding?.theme === THEME.DARK);
    }
  }, [lookUpResponse?.data?.branding?.theme]);

  const themeContextValue = useMemo(
    () => ({
      isDarkMode,
      setIsDarkMode,
    }),
    [isDarkMode, setIsDarkMode],
  );

  useEffect(() => {
    if (
      !lookUpResponse.isLoading &&
      pathname !== "/blocked" &&
      !isEmpty(userAccess) &&
      !getLookUpResponse?.menu &&
      !isLoggingOut
    ) {
      dispatch({
        type: CONTACT_PROFILE_LOOKUP,
        payload: {
          address: "lookupAfter",
        },
      });
    }
  }, [pathname, lookUpResponse, getLookUpResponse, isLoggingOut]);

  const hasFetchedAdvisorLookupRef = useRef(false);
  useEffect(() => {
    // Sidebar (and its own fetch of the advisor's menu) only mounts once this layout already
    // considers the route visible - on a hard refresh directly on an advisor-only route, that
    // never happens without fetching it here first. Fired at most once per "acting as advisor"
    // session - the fetch itself resets redux data to {} while in flight, so watching that data
    // directly (instead of using a one-shot ref) would re-trigger this effect forever.
    if (isActingAsSelectedAdvisor && !hasFetchedAdvisorLookupRef.current && !isLoggingOut) {
      hasFetchedAdvisorLookupRef.current = true;
      dispatch({
        type: AGENT_PROFILE_LOOKUP,
        payload: {
          address: "lookupAfterAgent",
        },
      });
    }
    if (!isActingAsSelectedAdvisor) {
      hasFetchedAdvisorLookupRef.current = false;
    }
  }, [isActingAsSelectedAdvisor, isLoggingOut]);

  const fetchPermissions = useAssistantPermissionsStore((state) => state.fetchPermissions);
  const clearPermissions = useAssistantPermissionsStore((state) => state.clearPermissions);
  // The permissions endpoint needs the UserAssistant link record id, not the advisor's own
  // user id (selectedAssistantAgentOption) - see the matching note in DashboardLeftPanel.tsx.
  const selectedAdvisorId = assistantAgentObject?.userAssistantId;
  // Refetched not just on app load/advisor-switch but on every route (path or query param)
  // change too, so permissions granted/revoked server-side while the assistant is mid-session
  // take effect as soon as they navigate, rather than only on the next hard refresh/switch.
  const hasFetchedPermissionsRef = useRef<string | null>(null);
  useEffect(() => {
    const fetchKey = `${selectedAdvisorId}|${pathname}|${search}`;
    if (isActingAsSelectedAdvisor && hasFetchedPermissionsRef.current !== fetchKey) {
      hasFetchedPermissionsRef.current = fetchKey;
      fetchPermissions(selectedAdvisorId);
    }
    if (!isActingAsSelectedAdvisor) {
      hasFetchedPermissionsRef.current = null;
      clearPermissions();
    }
  }, [
    isActingAsSelectedAdvisor,
    selectedAdvisorId,
    pathname,
    search,
    fetchPermissions,
    clearPermissions,
  ]);

  const sideArr =
    isActingAsSelectedAdvisor && Array.isArray(agentLookUpResponse?.data?.menu)
      ? agentLookUpResponse?.data?.menu
      : lookUpResponse?.data?.menu || getLookUpResponse?.menu;
  const sideBarUrls: string[] = sideArr?.reduce(
    (acc: string[], item: any) => acc?.concat(item?.groupItems?.map((it: any) => it?.link)),
    [],
  );
  sideBarUrls?.push(
    "/complete-your-purchase",
    "/profile",
    "/chat",
    "/notification",
    "/settings",
    "/agency",
    "/sub-agency",
    "/agent",
    "/subagencymanager",
    "/subagencyagents",
    "/subagencyassistants",
    "/assistant",
    "/requestProposal",
    "/editProfile",
    "/agentsubagencyassistants",
    "/agentsubagencyagents",
    "/agentsubagencymanager",
    "/financing",
    "/dashboard",
    "/v2-dashboard",
    "/feed",
  );

  if (userAccess?.userType !== USERTYPE.CONTACT) {
    sideBarUrls?.push("/proposal", "/pending", "/plans", "/financing", "/create-proposal");
  }
  const currentUrlStartWith = pathname.split("/")[1];

  const idx = sideBarUrls?.findIndex((el: string) => currentUrlStartWith === el?.slice(1));

  return (
    <ThemeContext.Provider value={themeContextValue}>
      {!sideArr?.length && lookUpResponse.isLoading ? (
        <div className="flex justify-center items-center h-screen">
          <ProgressSpinner
            style={{ width: "50px", height: "50px" }}
            strokeWidth="4"
            fill="transparent"
            animationDuration=".5s"
            className="p-progress-circle"
          />
        </div>
      ) : idx >= 0 ? (
        userAccess?.userType === "contact" ? (
          isNewDesign ? (
            <ContactV2Layout />
          ) : (
            <ContactLayout />
          )
        ) : (
          <>
            <div className="h-full flex">
              <Sidebar />
              <div
                className="flex-1 h-screen min-h-0 min-w-0 bg-[var(--surface-ground)] overflow-auto scrollbar-hide app-scroll-container"
                id={pathname.includes("academy") ? "course-list" : undefined}
              >
                <Outlet key={selectedAdvisorOutletKey} />
              </div>
            </div>
          </>
        )
      ) : (
        <Outlet key={selectedAdvisorOutletKey} />
      )}
    </ThemeContext.Provider>
  );
};

export default RootLayout;
